import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // No longer reading user from localStorage - fetched from server on mount
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingAs, setViewingAs] = useState(null); // { company_id, company_name }

  const loadCompanies = useCallback(async () => {
    try {
      const res = await api.get('/auth/my-companies');
      setCompanies(res.data);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  }, []);

  // On mount, check if the user has a valid session by calling /auth/me
  // The httpOnly cookie will be sent automatically
  useEffect(() => {
    api.get('/auth/me')
      .then(async (res) => {
        setUser(res.data);
        // Restore viewing-as state if admin is impersonating a client
        if (res.data.viewing_as) {
          setViewingAs(res.data.viewing_as);
        }
        try {
          const companiesRes = await api.get('/auth/my-companies');
          setCompanies(companiesRes.data);
        } catch (err) {
          // Non-critical
        }
      })
      .catch(() => {
        // No valid session - user needs to log in
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, totp_code) => {
    const res = await api.post('/auth/login', { email, password, totp_code });

    if (res.data.requires_2fa) {
      return { requires_2fa: true };
    }

    // Server sets httpOnly cookies - we just store the user data in state
    setUser(res.data.user);
    await loadCompanies();
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    setUser(res.data.user);
    await loadCompanies();
    return res.data.user;
  };

  // Used after accepting invite or resetting password
  // Server sets httpOnly cookies - we just store user data in state
  const loginWithToken = async (userData) => {
    setUser(userData);
    await loadCompanies();
  };

  const switchCompany = async (companyId) => {
    try {
      const res = await api.post('/auth/switch-company', { company_id: companyId });
      setUser(res.data.user);
      // Reload data for the new company context
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to switch company:', err);
    }
  };

  const viewAsClient = async (companyId) => {
    try {
      await api.post('/auth/view-as-client', { company_id: companyId });
      // Don't set viewingAs state here — it triggers AdminRoute's useEffect
      // which immediately calls exitViewAsClient before navigation happens.
      // Let /auth/me restore it after the page reload.
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to view as client:', err);
    }
  };

  const exitViewAsClient = async ({ redirect = true } = {}) => {
    try {
      const res = await api.post('/auth/exit-view-as-client');
      setUser(res.data.user);
      setViewingAs(null);
      if (redirect) window.location.href = '/admin/clients';
    } catch (err) {
      console.error('Failed to exit view as client:', err);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Best-effort server-side logout
    }
    setUser(null);
    setCompanies([]);
  };

  return (
    <AuthContext.Provider value={{ user, companies, login, loginWithToken, register, logout, refreshUser, switchCompany, viewAsClient, exitViewAsClient, viewingAs, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
